import {
  Form,
  FormInput,
  FormField,
  Button,
  FormTextarea,
  FormItem,
  FormLabel,
  FormControl,
  cn,
  Input,
} from "web-design-system";
import { imageAssetUrl } from "@/utils/url";
import { useForm } from "react-hook-form";
import { UploadIcon } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import {
  useDistributorBranding,
  useDistributorProfile,
  useUpdateDistributorBranding,
} from "creso-core/hooks";
import { distributorBrandingSchema } from "creso-core/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { DistributorBrandingFormValues } from "creso-core/types";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { useAuthContext } from "@/providers/auth-provider";
import { isEmployeeUser } from "creso-core/utils";
import { useUnstableFeatures } from "@core/hooks/use-unstable-features";
import { EditableLink } from "../components/editable-link";
import { LoginBannerDialog } from "../components/login-banner/login-banner-dialog";
import { getLoginBannerUrl } from "../components/login-banner/banners";
import { BrandingPreview } from "../components/preview/branding-preview";
import { BrandingPreviewValues } from "../components/preview/types";
import { LegacyBranding } from "./branding-dep";

const DownloadVCard = lazy(() => import("../components/vcard"));

const CLIENT_APP_DOMAIN = "creso.in";
// Also used for the client web app subdomain, so it must be a valid DNS label
const LINK_NAME_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

export function Branding() {
  const { customBranding } = useUnstableFeatures();
  return customBranding ? <CustomBranding /> : <LegacyBranding />;
}

function CustomBranding() {
  const { data: branding } = useDistributorBranding();
  const { data: profile } = useDistributorProfile();
  const { mutateAsync: updateDistributorBranding, isPending: isUpdating } =
    useUpdateDistributorBranding();
  const { employee, user } = useAuthContext();
  const isEmployee = isEmployeeUser(user.user_type);
  const handle = profile?.handle ?? "";
  // Until a subdomain is saved, the web app is served on the handle
  const subdomain = branding?.subdomain_name || handle;
  const webAppUrl = `https://${subdomain}.${CLIENT_APP_DOMAIN}`;
  const onboardingLinkPrefix = `${webAppUrl}/onboard`;
  const onboardingUrl = `${onboardingLinkPrefix}${isEmployee && employee?.id ? `?employee_id=${employee?.id}` : ""}`;

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isLoginBannerDialogOpen, setIsLoginBannerDialogOpen] = useState(false);
  const loginBannerUrl = getLoginBannerUrl(branding);

  const form = useForm<DistributorBrandingFormValues>({
    values: {
      brand_name: branding?.brand_name || "",
      tag_line: branding?.tag_line || "",
      profile_bio: branding?.profile_bio || "",
      brand_logo: branding?.brand_logo || "",
      brand_qr: branding?.brand_qr || "",
      id: branding?.id || 0,
      distributor: branding?.distributor || 0,
    },
    resolver: zodResolver(distributorBrandingSchema),
  });

  const { isDirty } = form.formState;

  const [brandName, tagLine, profileBio] = form.watch([
    "brand_name",
    "tag_line",
    "profile_bio",
  ]);
  const previewValues: BrandingPreviewValues = {
    brandName,
    tagLine,
    profileBio,
    logoUrl: logoPreview ?? (branding?.brand_logo as string),
    distributorName: profile?.name ?? "",
    arnCode: profile?.arn_code ?? "",
    qrUrl: branding?.brand_qr
      ? `${branding.brand_qr}?q=${branding.modified_at}`
      : undefined,
    loginBanner: { url: loginBannerUrl },
  };

  const onSubmit = async (data: DistributorBrandingFormValues) => {
    const formData = new FormData();

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const value = data[key as keyof DistributorBrandingFormValues];
      if (key === "brand_logo" && (value as unknown) instanceof File) {
        formData.append(key, value as File);
      } else {
        formData.append(key, String(value));
      }
    });

    try {
      await updateDistributorBranding(formData);
      toast.success("Profile updated successfully");
    } catch (error) {
      handleError(error, "Failed to update branding information");
    }
  };

  const validateLinkName = (value: string) => {
    if (!value) throw new Error("Link is required");
    if (!LINK_NAME_REGEX.test(value)) {
      throw new Error(
        "Only letters, numbers and - are allowed, and it can't start or end with -"
      );
    }
  };

  const saveSubdomain = async (value: string) => {
    validateLinkName(value);
    const formData = new FormData();
    formData.append("subdomain_name", value);
    try {
      await updateDistributorBranding(formData);
    } catch {
      throw new Error("This link is not available");
    }
  };

  const handleLogoUpload = (file: File) => {
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
    form.setValue("brand_logo", file as string & File, { shouldDirty: true });
  };

  return (
    <div className="size-full p-5 flex gap-8">
      <Form {...form}>
        <form
          className="flex flex-col gap-4 w-full max-w-2xl xl:max-w-3xl shrink-0"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <span className="font-semibold text-lg">Client app branding</span>
          <div className="flex items-start gap-5">
            <FormField
              control={form.control}
              className="flex-1"
              name="brand_name"
              render={({ field }) => (
                <FormInput {...field} label="Brand name" />
              )}
            />
            <FormField
              control={form.control}
              className="flex-1"
              name="tag_line"
              render={({ field }) => <FormInput {...field} label="Tag line" />}
            />
          </div>

          <div className="flex gap-5">
            <FormField
              control={form.control}
              name="brand_logo"
              render={({ field }) => (
                <FormItem className="shrink-0">
                  <FormLabel className="sr-only">Brand logo</FormLabel>
                  <FormControl>
                    <div className="size-36 p-2 group rounded-2xl relative border border-dashed border-border">
                      {(logoPreview || branding?.brand_logo) && (
                        <img
                          src={logoPreview ?? (branding?.brand_logo as string)}
                          alt="Brand logo"
                          className="size-full object-contain group-hover:opacity-50 rounded-xl transition-all duration-300"
                        />
                      )}
                      <UploadIcon
                        className={cn(
                          "absolute stroke-foreground/50 size-6 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                          !logoPreview && !branding?.brand_logo && "opacity-100"
                        )}
                      />
                      <Input
                        {...field}
                        value=""
                        className="absolute size-full opacity-0 inset-0 cursor-pointer"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleLogoUpload(file);
                          }
                        }}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              className="flex-1"
              control={form.control}
              name="profile_bio"
              render={({ field }) => (
                <FormTextarea
                  {...field}
                  className="min-h-30"
                  label="Profile bio"
                />
              )}
            />
          </div>

          {isDirty && (
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                type="button"
                size="sm"
                onClick={() => {
                  form.reset();
                  setLogoPreview(null);
                }}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button size="sm" type="submit" disabled={isUpdating}>
                {isUpdating ? "Saving..." : "Save"}
              </Button>
            </div>
          )}

          <div className="h-px my-2 w-full bg-border" />

          <span className="font-semibold text-lg">Web app configuration</span>
          <EditableLink
            label="Web app link"
            url={webAppUrl}
            value={subdomain}
            suffix={`.${CLIENT_APP_DOMAIN}`}
            copyText={webAppUrl}
            onSave={saveSubdomain}
          />

          <div className="flex items-center gap-5 rounded-lg bg-card p-2">
            <img
              src={loginBannerUrl}
              alt="Current login banner"
              className="w-40 aspect-[5/4] shrink-0 rounded-xl object-cover"
            />
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold text-sm">Login banner</span>
              <span className="text-sm text-muted-foreground">
                Your client will see this image when logging in to our web app.
              </span>
              <Button
                type="button"
                variant="outline"
                className="mt-2"
                onClick={() => setIsLoginBannerDialogOpen(true)}
              >
                Customize
              </Button>
            </div>
          </div>

          <div className="h-px w-full bg-border" />

          <EditableLink
            label="Client onboarding link"
            url={`${onboardingLinkPrefix}`}
            value={handle}
            prefix={onboardingLinkPrefix}
            copyText={onboardingUrl}
          />

          <div className="flex gap-4 pb-4">
            <div className="flex flex-1 justify-between rounded-2xl bg-linear-to-b from-slate-700 to-slate-900 text-white overflow-hidden">
              <div className="p-5 flex flex-col justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <h5>Stand out</h5>
                  <h4>Share your brand with a digital touch.</h4>
                </div>
                <Suspense fallback={null}>
                  <DownloadVCard />
                </Suspense>
              </div>
              <img
                src={imageAssetUrl("card-hand.webp")}
                alt="Hand holding a phone showing a VCard"
                className="max-h-40 self-end"
              />
            </div>
            {branding?.brand_qr && (
              <div className="shrink-0 rounded-2xl bg-white border border-border flex items-center justify-center p-2">
                <img
                  className="size-36 object-contain"
                  src={`${branding.brand_qr}?q=${branding.modified_at}`}
                  alt="Brand QR code"
                />
              </div>
            )}
          </div>
        </form>
      </Form>

      {/* Sticks below the page navbar (68px) and fills the rest of the scroll viewport */}
      <aside className="flex-1 flex gap-4 min-w-0 flex-col self-start sticky top-17 h-[calc(100dvh-6.5rem)] rounded-lg bg-secondary">
        <span className="px-6 pt-6 font-semibold text-lg">Preview</span>
        {/* The preview keeps its own margin, for the section hover highlights */}
        <div className="flex-1 min-h-0 px-2 pb-4">
          <BrandingPreview {...previewValues} />
        </div>
      </aside>

      <LoginBannerDialog
        open={isLoginBannerDialogOpen}
        onOpenChange={setIsLoginBannerDialogOpen}
        branding={branding}
        previewValues={previewValues}
      />
    </div>
  );
}
